import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import './index.less';
import { API, Logger } from 'aws-amplify';
import { Select } from 'antd';
import moment from 'moment';
import Context from '../../context';
import SummaryTable from '../SummaryTable';
import StatusIcon from '../StatusIcon';
import {
  formatDateTimeStr,
  formatDate,
  formatNumber,
} from '../../utils/i18n';
import { STATUS_NAMES } from '../../constants/status';

const YEAR_FILTER_ALL = 'ALL_YEARS';

const CapacitySubscriptionsTable = ({
  includeTimestamps,
  newCapacitySubscription,
  title,
  stackedHeader,
}) => {
  const { selectedMarketPartyId } = useContext(Context);
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [subscriptions, setSubscriptions] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState(STATUS_NAMES);
  const [selectedYear, setSelectedYear] = useState(YEAR_FILTER_ALL);

  const fetchSubscriptions = useCallback(async () => {
    if (!selectedMarketPartyId) {
      setSubscriptions([]);
      return;
    }

    setIsLoading(true);
    try {
      const response = await API.get(
        'FINTSO',
        `/marketparties/${selectedMarketPartyId}/capacity-subscriptions`,
      );

      const items = Array.isArray(response?.items) ? response.items : [];
      setSubscriptions(items);
    } catch (error) {
      Logger.error('Error fetching capacity subscriptions', error);
      setSubscriptions([]);
    } finally {
      setIsLoading(false);
    }
  }, [selectedMarketPartyId]);

  useEffect(() => {
    fetchSubscriptions();
  }, [fetchSubscriptions]);

  useEffect(() => {
    if (newCapacitySubscription === null) {
      return;
    }

    // After a successful save, refetch the full list from the backend
    fetchSubscriptions();
  }, [fetchSubscriptions, newCapacitySubscription]);

  const mapStatusToLabel = useCallback((status) => (
    status ? t(`common.status.${status}`) : status
  ), [t]);

  const tableHeaders = useMemo(() => {
    const headers = [];
    if (includeTimestamps) {
      headers.push({
        title: 'created',
        dataIndex: 'createdAt',
        key: 'createdAt',
      });
    }

    headers.push(
      /*       {
        title: {
          key: 'connectionPoint',
        },
        dataIndex: 'connectionPoint',
        key: 'connectionPoint',
      }, */
      {
        title: {
          key: 'periodInGasDays',
        },
        dataIndex: 'period',
        key: 'period',
      },
      {
        title: {
          key: 'subscribedCapacity',
        },
        dataIndex: 'reservedCapacity',
        key: 'reservedCapacity',
      },
      {
        title: {
          key: 'status',
        },
        dataIndex: 'status',
        key: 'status',
        render: (status) => (
          <StatusIcon
            status={status}
          />
        ),
      },
    );

    return headers;
  }, [includeTimestamps, mapStatusToLabel]);

  const connectionPointLabel = useMemo(() => (
    t('capacity.point.exitZone')
  ), [t]);

  const availableYears = useMemo(() => (
    subscriptions
      .map((subscription) => {
        if (!subscription.startDate) {
          return null;
        }

        const startDate = typeof subscription.startDate === 'number'
          ? subscription.startDate * 1000
          : subscription.startDate;

        const year = moment(startDate).utc().year();
        return year;
      })
      .filter((year, index, array) => year && array.indexOf(year) === index)
      .sort((a, b) => a - b)
  ), [subscriptions]);

  const tableData = useMemo(() => (
    subscriptions
      .filter((subscription) => {
        const matchesStatus = selectedStatus.includes(subscription.status);

        if (!selectedYear) {
          return matchesStatus;
        }

        const startDate = typeof subscription.startDate === 'number'
          ? subscription.startDate * 1000
          : subscription.startDate;

        const startYear = startDate
          ? moment(startDate).utc().year()
          : null;

        const matchesYear = selectedYear === YEAR_FILTER_ALL || startYear === selectedYear;

        return matchesStatus && matchesYear;
      })
      .map((subscription) => {
        const fixTimestamp = (ts) => {
          if (typeof ts === 'number') {
            return ts * 1000;
          }
          return ts;
        };

        const startDate = fixTimestamp(subscription.startDate);
        const endDate = fixTimestamp(subscription.endDate);
        const createdAt = subscription.createdAt
          ? moment(fixTimestamp(subscription.createdAt)).toISOString()
          : null;

        const start = startDate
          ? formatDate(startDate)
          : '--';
        const end = endDate
          ? formatDate(endDate)
          : '--';

        const capacityMWhPerHour = subscription.subscribedCapacity != null
          ? Number(subscription.subscribedCapacity) / 1000
          : null;

        return {
          id: subscription.id,
          createdAt: formatDateTimeStr(createdAt, '--'),
          connectionPoint: connectionPointLabel,
          period: `${start} - ${end}`,
          reservedCapacity: capacityMWhPerHour !== null
            ? `${formatNumber(capacityMWhPerHour, 0, 3)} MWh/h`
            : '--',
          status: subscription.status,
        };
      })
  ), [
    connectionPointLabel,
    formatNumber,
    formatDateTimeStr,
    selectedStatus,
    selectedYear,
    subscriptions,
  ]);

  const renderStatusOptions = useCallback(() => (
    STATUS_NAMES.map((status) => (
      <Select.Option key={status} value={status}>
        {t(`common.label.${status}`)}
      </Select.Option>
    ))
  ), [t]);

  const renderYearOptions = useCallback(() => (
    [
      <Select.Option key={YEAR_FILTER_ALL} value={YEAR_FILTER_ALL}>
        {t('capacitySubscription.table.filters.yearAll')}
      </Select.Option>,
      ...availableYears.map((year) => (
        <Select.Option key={year} value={year} width={80}>
          {year}
        </Select.Option>
      )),
    ]
  ), [availableYears, t]);

  const headerActions = (
    <div className="capacitysubscriptionstable__filters">
      <div className="status-selector">
        <div className="title">
          {t('common.table.header.status')}
        </div>
        <Select
          mode="multiple"
          value={selectedStatus}
          onChange={(items) => setSelectedStatus(items.length ? items : STATUS_NAMES)}
        >
          {renderStatusOptions()}
        </Select>
      </div>
      <div className="separator" />
      <div className="year-selector">
        <div className="title">
          {t('capacitySubscription.form.label.year')}
        </div>
        <Select
          value={selectedYear}
          onChange={(value) => setSelectedYear(value || YEAR_FILTER_ALL)}
        >
          {renderYearOptions()}
        </Select>
      </div>
    </div>
  );

  return (
    <SummaryTable
      className="capacitysubscriptionstable"
      tableHeaders={tableHeaders}
      tableData={tableData}
      isLoading={isLoading}
      headerActions={headerActions}
      title={title}
      stackedHeader={stackedHeader}
    />
  );
};

CapacitySubscriptionsTable.propTypes = {
  includeTimestamps: PropTypes.bool,
  newCapacitySubscription: PropTypes.shape({
    id: PropTypes.string,
  }),
  title: PropTypes.string,
  stackedHeader: PropTypes.bool,
};

CapacitySubscriptionsTable.defaultProps = {
  includeTimestamps: false,
  newCapacitySubscription: null,
  title: '',
  stackedHeader: false,
};

CapacitySubscriptionsTable.displayName = 'CapacitySubscriptionsTable';

export default CapacitySubscriptionsTable;
